import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, FailureNotice, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { InstitutionName, InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { useToast } from '@/components/ui/Toast';
import { platformApi } from '@/api/platform';
import { ApiError } from '@/api/client';
import { studioApi } from '@/api/studio';
import { useAsyncData } from '@/hooks';
import {
  PLATFORM_OWNED_SCOPES,
  VERSION_ACTIONS,
  type ConfigurationKeyDescriptor,
  type ConfigurationKeysResponse,
  type ConfigurationLayer,
  type ConfigurationLayerScope,
  type ConfigurationValueMap,
  type ConfigurationVersion,
  type ConfigurationVersionState,
  type InstitutionConfigurationRecord,
  type ResolvedConfiguration,
  EDUCATION_SUBSYSTEMS,
  INSTITUTION_TYPES,
  OWNERSHIP_MODELS,
  type ResolvedConfigurationValue,
  type VersionAction,
} from '@/types/studio';
import type { Campus } from '@/types/platform';
import type { AsyncState } from '@/hooks';
import { formatDateTime } from '@/utils/format';
import { humanizeValue } from './provenance-ladder';
import { ProvenanceLadder } from './ProvenanceLadder';
import shared from './platform-pages.module.css';
import own from './ConfigurationStudioPage.module.css';

/**
 * The Configuration Studio: what an institution's operating model IS, WHY each value is what
 * it is, and the governed path to changing it.
 *
 * THREE SURFACES, ONE PAGE. The effective view answers "why is my calendar semester-based"
 * with the full layer ladder, because the backend's provenance contract says plainly that an
 * administrator shown only the winner edits the wrong layer and concludes the platform is
 * broken. The versions panel is the change process — draft, validate, review, approve,
 * schedule, activate — and its buttons are the legal moves and nothing else, mirroring
 * InstitutionProductsPanel: a button that produces a 400 teaches an operator that the rules
 * are arbitrary, because the refusal arrives after they have decided. The layers panel (on
 * the chooser screen, because shared layers belong to no single institution) edits the
 * platform-owned defaults the ladder resolves through.
 *
 * ERROR PREVENTION OVER RECOVERY, throughout. Every value input is a Select constrained to
 * the registry's closed sets — an enum is never free-typed. "Inherit" is the explicit empty
 * option, because an absent override and an override of the empty string are different
 * statements and only the first one is legal. The one genuinely destructive-feeling act,
 * activation, goes through the standard confirm gate with its impact stated.
 */

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Tone per lifecycle state, in the console's shared vocabulary (see StateBadge). */
const VERSION_TONES: Record<ConfigurationVersionState, string> = {
  DRAFT: 'neutral',
  VALIDATION_FAILED: 'critical',
  READY_FOR_REVIEW: 'info',
  APPROVED: 'progress',
  SCHEDULED: 'progress',
  ACTIVE: 'positive',
  SUPERSEDED: 'neutral',
};

/** States that count as "a draft is already open" for the create-draft guard. */
const OPEN_STATES: readonly ConfigurationVersionState[] = [
  'DRAFT',
  'VALIDATION_FAILED',
  'READY_FOR_REVIEW',
];

export function ConfigurationStudioPage() {
  const { institutionId = '' } = useParams();

  if (institutionId.length === 0) {
    return <StudioChooser />;
  }

  // A deep link can carry anything — a bookmark, a hand-edited URL, a school's name. The
  // API validates the segment as a UUID, so a non-id would surface as the raw "Validation
  // failed (uuid is expected)". Answer with the chooser and a sentence in the operator's
  // terms instead. (Same guard as InstitutionIntegrationsPage.)
  if (!UUID_SHAPE.test(institutionId)) {
    return <StudioChooser rejected={institutionId} />;
  }

  return <StudioBody institutionId={institutionId} />;
}

/**
 * No institution chosen yet. A separate state rather than an empty page, because "nothing
 * selected" and "this institution has no configuration" are different answers.
 *
 * The shared layers panel lives HERE, not under an institution: a country default belongs
 * to every school in the country, and parking it under one school's URL would imply edits
 * there affect only that school — the exact misreading the provenance view exists to kill.
 */
function StudioChooser({ rejected }: { rejected?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={shared.page}>
      <header className={shared.pageHeader}>
        <div>
          <h1 className={shared.pageTitle}>{t('studio.pageTitle')}</h1>
          <p className={shared.pageLead}>{t('studio.pageLead')}</p>
        </div>
      </header>

      <Panel title={t('studio.chooseTitle')} icon="institution">
        {rejected && (
          <p className={shared.rowDetailError} role="alert">
            {t('studio.notAnInstitutionId', { value: rejected })}
          </p>
        )}
        <div className={shared.filterBar}>
          <div className={shared.filterField}>
            <label className={shared.filterLabel} htmlFor="studio-institution">
              {t('studio.institutionLabel')}
            </label>
            <InstitutionPicker
              id="studio-institution"
              value=""
              onChange={(next) => {
                if (next) navigate(`/admin/institutions/${encodeURIComponent(next)}/configuration`);
              }}
            />
          </div>
        </div>
        <p className={shared.sectionEmpty} role="status">
          {t('studio.chooseBody')}
        </p>
      </Panel>

      <LayersAdminPanel />
    </div>
  );
}

/* ─── One institution ─── */

function StudioBody({ institutionId }: { institutionId: string }) {
  const { t } = useTranslation();

  // The key registry drives every screen below — labels, closed value sets, campus
  // defaults — so it loads once here and is threaded down. One cache key app-wide.
  const keysFetcher = useCallback(() => studioApi.keys(), []);
  const { state: keysState, reload: reloadKeys } = useAsyncData<ConfigurationKeysResponse>(
    'studio-keys',
    keysFetcher,
  );

  return (
    <div className={shared.page}>
      <header className={shared.pageHeader}>
        <div>
          <h1 className={shared.pageTitle}>{t('studio.pageTitle')}</h1>
          <p className={shared.pageLead}>{t('studio.pageLead')}</p>
        </div>
      </header>

      <nav className={shared.breadcrumb} aria-label={t('studio.breadcrumbLabel')}>
        <Link to="/admin/institutions">{t('platform.nav.institutions')}</Link>
        <Icon name="chevronRight" size={13} />
        {/* The school's name, not its UUID — the id tells an operator nothing. */}
        <Link to={`/admin/institutions/${encodeURIComponent(institutionId)}`}>
          <InstitutionName institutionId={institutionId} />
        </Link>
        <Icon name="chevronRight" size={13} />
        <span>{t('studio.breadcrumbHere')}</span>
      </nav>

      <AsyncPanel state={keysState} onRetry={reloadKeys}>
        {(registry) => <StudioPanels institutionId={institutionId} registry={registry} />}
      </AsyncPanel>
    </div>
  );
}

function StudioPanels({
  institutionId,
  registry,
}: {
  institutionId: string;
  registry: ConfigurationKeysResponse;
}) {
  // Whether this institution HAS a configuration record at all. An institution
  // created before the configuration work has none — its type, subsystem and
  // country were never recorded, so the resolver has nothing to resolve
  // against and every panel below would answer with that riddle. 404 here is a
  // STATE (offer the onboarding form), not a failure.
  const configFetcher = useCallback(() => studioApi.configuration(institutionId), [institutionId]);
  const { state: configState, reload: reloadConfig } = useAsyncData<InstitutionConfigurationRecord>(
    `studio-config:${institutionId}`,
    configFetcher,
  );

  if (configState.status === 'error' && (configState.error as ApiError)?.status === 404) {
    return <ConfigureInstitutionCard institutionId={institutionId} onConfigured={reloadConfig} />;
  }

  return (
    <AsyncPanel state={configState} onRetry={reloadConfig}>
      {(config) => (
        <>
          <AcademicSeedStrip config={config} onRetried={reloadConfig} />
          <ConfiguredStudioPanels institutionId={institutionId} registry={registry} />
        </>
      )}
    </AsyncPanel>
  );
}

/**
 * Whether EduCore actually received the academic hand-off — answered on screen
 * instead of in a log grep. The configure call reports a failed seeding only as
 * a toast warning, which is gone by the next visit; the record keeps the truth
 * and this strip shows it, with the retry a click instead of a hand-built POST.
 */
function AcademicSeedStrip({
  config,
  onRetried,
}: {
  config: InstitutionConfigurationRecord;
  onRetried: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [retrying, setRetrying] = useState(false);

  // SENT and NOT_APPLICABLE are quiet states; a strip repeating "everything is
  // fine" on every visit teaches operators to stop reading it.
  if (config.academicSeedStatus === 'SENT' || config.academicSeedStatus === 'NOT_APPLICABLE') {
    return null;
  }

  const retry = async () => {
    setRetrying(true);
    try {
      const outcome = await studioApi.retrySeeding(config.institutionId);
      for (const warning of outcome.warnings) toast.info(warning);
      if (outcome.warnings.length === 0) toast.success(t('studio.seeding.retried'));
      onRetried();
    } catch (cause) {
      toast.error((cause as Error).message);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <p className={shared.rowDetailError} role="status">
      <Icon name="error" size={15} aria-hidden="true" />{' '}
      {config.academicSeedStatus === 'FAILED'
        ? t('studio.seeding.failed')
        : t('studio.seeding.pending')}
      {config.academicSeedError ? ` — ${config.academicSeedError}` : ''}{' '}
      <button
        type="button"
        className={shared.secondaryBtn}
        disabled={retrying}
        onClick={() => void retry()}
      >
        {retrying ? t('common.loading') : t('studio.seeding.retry')}
      </button>
    </p>
  );
}

/**
 * The onboarding form for an institution that predates the configuration work.
 * Four selections — what the institution IS — and the platform's layer ladder
 * takes over from there. Idempotent server-side; the academic seeding to
 * EduCore rides the same call.
 */
function ConfigureInstitutionCard({
  institutionId,
  onConfigured,
}: {
  institutionId: string;
  onConfigured: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState({
    institutionType: '',
    educationSubsystem: '',
    ownershipModel: '',
    countryCode: 'CM',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready =
    !!form.institutionType &&
    !!form.educationSubsystem &&
    !!form.ownershipModel &&
    /^[A-Za-z]{2}$/.test(form.countryCode);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const outcome = await studioApi.configure({
        institutionId,
        institutionType: form.institutionType,
        educationSubsystem: form.educationSubsystem,
        ownershipModel: form.ownershipModel,
        countryCode: form.countryCode.toUpperCase(),
      });
      for (const warning of outcome.warnings) toast.info(warning);
      toast.success(t('studio.configure.done'));
      onConfigured();
    } catch (cause) {
      // The server's sentence, verbatim — it names what actually refused.
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const option = (value: string) => ({
    value,
    label: t(`studio.configure.values.${value}`, { defaultValue: value }),
  });

  return (
    <Panel title={t('studio.configure.title')} icon="institution">
      <p className={shared.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('studio.configure.lead')}
      </p>
      {error && (
        <p className={shared.rowDetailError} role="alert">
          {error}
        </p>
      )}
      <div className={shared.filterBar}>
        <div className={shared.filterField}>
          <label className={shared.filterLabel} htmlFor="cfg-type">
            {t('studio.configure.type')}
          </label>
          <Select
            id="cfg-type"
            searchable
            value={form.institutionType}
            onChange={(value) => setForm({ ...form, institutionType: value })}
            placeholder={t('studio.configure.typePlaceholder')}
            options={INSTITUTION_TYPES.map(option)}
          />
        </div>
        <div className={shared.filterField}>
          <label className={shared.filterLabel} htmlFor="cfg-subsystem">
            {t('studio.configure.subsystem')}
          </label>
          <Select
            id="cfg-subsystem"
            value={form.educationSubsystem}
            onChange={(value) => setForm({ ...form, educationSubsystem: value })}
            placeholder={t('studio.configure.subsystemPlaceholder')}
            options={EDUCATION_SUBSYSTEMS.map(option)}
          />
        </div>
        <div className={shared.filterField}>
          <label className={shared.filterLabel} htmlFor="cfg-ownership">
            {t('studio.configure.ownership')}
          </label>
          <Select
            id="cfg-ownership"
            value={form.ownershipModel}
            onChange={(value) => setForm({ ...form, ownershipModel: value })}
            placeholder={t('studio.configure.ownershipPlaceholder')}
            options={OWNERSHIP_MODELS.map(option)}
          />
        </div>
        <div className={shared.filterField}>
          <label className={shared.filterLabel} htmlFor="cfg-country">
            {t('studio.configure.country')}
          </label>
          <input
            id="cfg-country"
            className={shared.input}
            value={form.countryCode}
            maxLength={2}
            onChange={(event) => setForm({ ...form, countryCode: event.target.value })}
            aria-describedby="cfg-country-hint"
          />
          <span className={shared.fieldHint} id="cfg-country-hint">
            {t('studio.configure.countryHint')}
          </span>
        </div>
      </div>
      <div className={shared.panelActions}>
        <button
          type="button"
          className={shared.primaryBtn}
          disabled={!ready || submitting}
          onClick={() => void submit()}
        >
          {submitting ? t('common.loading') : t('studio.configure.submit')}
        </button>
      </div>
    </Panel>
  );
}

function ConfiguredStudioPanels({
  institutionId,
  registry,
}: {
  institutionId: string;
  registry: ConfigurationKeysResponse;
}) {
  const [campusId, setCampusId] = useState('');
  // The versions panel reloads the effective view after an activation, because the
  // activation is precisely the moment the effective view's answer changes.
  const resolvedFetcher = useCallback(
    () => studioApi.resolved(institutionId, campusId || undefined),
    [institutionId, campusId],
  );
  const { state: resolvedState, reload: reloadResolved } = useAsyncData<ResolvedConfiguration>(
    `studio-resolved:${institutionId}:${campusId}`,
    resolvedFetcher,
  );

  const active = resolvedState.status === 'ready' ? resolvedState.data : null;

  return (
    <>
      <EffectivePanel
        institutionId={institutionId}
        registry={registry}
        campusId={campusId}
        onCampusChange={setCampusId}
        state={resolvedState}
        onRetry={reloadResolved}
      />
      <VersionsPanel
        institutionId={institutionId}
        registry={registry}
        activeResolved={active}
        onActivated={reloadResolved}
      />
    </>
  );
}

/* ─── Effective configuration with provenance ─── */

function EffectivePanel({
  institutionId,
  registry,
  campusId,
  onCampusChange,
  state,
  onRetry,
}: {
  institutionId: string;
  registry: ConfigurationKeysResponse;
  campusId: string;
  onCampusChange: (campusId: string) => void;
  state: AsyncState<ResolvedConfiguration>;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation();

  // The campus list comes from the structure endpoint, which exists for every institution
  // the platform bootstrapped. If it cannot be read, the campus switch is simply absent —
  // the institution-wide view stands on its own — rather than a broken control.
  const campusesFetcher = useCallback(
    () => platformApi.structure.get(institutionId).then((structure) => structure.campuses),
    [institutionId],
  );
  const { state: campusesState } = useAsyncData<Campus[]>(
    `studio-campuses:${institutionId}`,
    campusesFetcher,
  );
  const campuses = campusesState.status === 'ready' ? campusesState.data : [];

  return (
    <Panel
      title={t('studio.effective.title')}
      icon="settings"
      meta={
        state.status === 'ready' && state.data.versionNumber !== null
          ? t('studio.effective.versionMeta', { number: state.data.versionNumber })
          : undefined
      }
      actions={
        campuses.length > 0 ? (
          <div className={own.campusField}>
            <label className={shared.filterLabel} id="studio-campus-label">
              {t('studio.effective.campusLabel')}
            </label>
            <Select
              aria-labelledby="studio-campus-label"
              value={campusId}
              onChange={onCampusChange}
              options={[
                { value: '', label: t('studio.effective.wholeInstitution') },
                ...campuses.map((campus) => ({ value: campus.id, label: campus.name })),
              ]}
            />
          </div>
        ) : undefined
      }
    >
      {state.status === 'loading' && (
        <p className={shared.sectionEmpty} role="status">
          {t('common.loading')}
        </p>
      )}

      {/*
        404 here is NOT a missing endpooint and must not read as one: the controller answers
        404 when the institution has no ACTIVE version yet, which is the normal state of a
        school mid-onboarding. AsyncPanel's generic classifier would say "not deployed yet",
        which is a different and wrong claim, so this panel answers it itself.
      */}
      {state.status === 'error' &&
        (state.error.status === 404 ? (
          <div className={own.emptyState} role="status">
            <p className={own.emptyTitle}>{t('studio.effective.noActiveTitle')}</p>
            <p className={own.emptyBody}>{t('studio.effective.noActiveBody')}</p>
          </div>
        ) : (
          <FailureNotice error={state.error} onRetry={onRetry} />
        ))}

      {state.status === 'ready' && (
        <>
          <p className={shared.dataNotice}>
            <Icon name="info" size={15} aria-hidden="true" />
            {campusId
              ? t('studio.effective.campusNotice')
              : t('studio.effective.frozenNotice', {
                  when: formatDateTime(state.data.resolvedAt, i18n.language),
                })}
          </p>
          <ul className={own.keyList}>
            {registry.keys.map((descriptor) => (
              <EffectiveRow
                key={descriptor.key}
                descriptor={descriptor}
                resolved={state.data.values[descriptor.key]}
              />
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}

/**
 * One key: the value in force, where it came from, and the expandable "why".
 *
 * The ladder is collapsed by default — nine open ladders is a wall — but the SOURCE is
 * always visible, because "which layer said so" is the question this screen exists for and
 * hiding it behind a click would bury the headline.
 */
function EffectiveRow({
  descriptor,
  resolved,
}: {
  descriptor: ConfigurationKeyDescriptor;
  resolved: ResolvedConfigurationValue | undefined;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (!resolved) {
    // The floor is required to be complete, so a missing entry is a server-side anomaly
    // worth naming — not silently skipping — but one key's absence must not hide the rest.
    return (
      <li className={own.keyRow}>
        <div className={own.keyMain}>
          <p className={own.keyLabel}>{descriptor.label}</p>
          <p className={shared.rowDetailError}>{t('studio.effective.unresolvedKey')}</p>
        </div>
      </li>
    );
  }

  return (
    <li className={own.keyRow}>
      <div className={own.keyMain}>
        <p className={own.keyLabel}>
          {descriptor.label}
          <span className={own.keyCode}>{descriptor.key}</span>
        </p>
        <p className={own.keySource}>
          {t('studio.effective.setBy', { layer: resolved.source.label })}
          <span className={own.scopeTag}>{t(`studio.scope.${resolved.source.scope}`)}</span>
        </p>
        {resolved.note && <p className={shared.rowDetail}>{t('studio.effective.note', { note: resolved.note })}</p>}
        <p className={shared.rowMetaLine}>
          {resolved.campusOverridable
            ? t('studio.effective.campusMay')
            : t('studio.effective.campusMayNot')}
          {resolved.campusOverridableSource
            ? ` · ${t('studio.effective.campusRuleBy', { layer: resolved.campusOverridableSource.label })}`
            : ''}
        </p>

        <button
          type="button"
          className={own.whyToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <Icon name="chevronDown" size={14} aria-hidden="true" />
          {expanded ? t('studio.effective.hideWhy') : t('studio.effective.showWhy')}
        </button>

        {expanded && (
          <div className={own.ladderWrap}>
            <ProvenanceLadder resolved={resolved} />
            {resolved.overriddenLayers.length > 0 && (
              <p className={shared.rowMetaLine}>
                {t('studio.effective.beatenCount', { total: resolved.overriddenLayers.length })}
              </p>
            )}
          </div>
        )}
      </div>
      <div className={shared.rowAside}>
        {/* The humanized value carries the wire code on title: operators cross-reference
            the code against core's logs, and dropping it costs them a lookup. */}
        <span className={own.valueChip} title={resolved.value}>
          {humanizeValue(resolved.value)}
        </span>
      </div>
    </li>
  );
}

/* ─── Versions ─── */

function VersionBadge({ state }: { state: ConfigurationVersionState }) {
  const { t } = useTranslation();
  return (
    <span className={`${own.badge} ${own[`badge_${VERSION_TONES[state]}`]}`} data-state={state}>
      {t(`studio.state.${state}`)}
    </span>
  );
}

function VersionsPanel({
  institutionId,
  registry,
  activeResolved,
  onActivated,
}: {
  institutionId: string;
  registry: ConfigurationKeysResponse;
  activeResolved: ResolvedConfiguration | null;
  onActivated: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const fetcher = useCallback(() => studioApi.versions.list(institutionId), [institutionId]);
  const { state, reload } = useAsyncData<ConfigurationVersion[]>(
    `studio-versions:${institutionId}`,
    fetcher,
  );

  const [editing, setEditing] = useState<ConfigurationVersion | null>(null);
  const [previewing, setPreviewing] = useState<ConfigurationVersion | null>(null);
  const [scheduling, setScheduling] = useState<ConfigurationVersion | null>(null);
  const [approving, setApproving] = useState<ConfigurationVersion | null>(null);
  const [activating, setActivating] = useState<ConfigurationVersion | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  const versions = state.status === 'ready' ? state.data : [];
  const openDraft = versions.find((version) => OPEN_STATES.includes(version.state));
  const activeVersion = versions.find((version) => version.state === 'ACTIVE');

  /**
   * Opens a draft seeded from the version in force (the backend's default when no overrides
   * are sent), then puts the editor straight on it — creating and then hunting the list for
   * what you just created is two steps where one was asked for.
   */
  const createDraft = async () => {
    setCreating(true);
    setRowError(null);
    try {
      const draft = await studioApi.versions.createDraft(institutionId, {});
      reload();
      setEditing(draft);
    } catch (cause) {
      toast.error((cause as Error).message);
    } finally {
      setCreating(false);
    }
  };

  /**
   * Submit runs inline — it is not destructive and its interesting outcome is a STATE, not
   * a confirmation: the backend answers VALIDATION_FAILED as a version, and the row then
   * shows the reasons. An operator must see that as "the draft needs work", never as "the
   * request failed".
   */
  const submit = async (version: ConfigurationVersion) => {
    setBusyId(version.id);
    setRowError(null);
    try {
      const result = await studioApi.versions.submit(institutionId, version.id);
      reload();
      if (result.state === 'VALIDATION_FAILED') {
        toast.error(t('studio.versions.validationFailedToast', { number: result.versionNumber }));
      } else {
        toast.success(t('studio.versions.submittedToast', { number: result.versionNumber }));
      }
    } catch (cause) {
      setRowError({ id: version.id, message: (cause as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  const approve = async () => {
    if (!approving) return;
    setBusyId(approving.id);
    try {
      const result = await studioApi.versions.approve(institutionId, approving.id);
      setApproving(null);
      reload();
      toast.success(t('studio.versions.approvedToast', { number: result.versionNumber }));
    } catch (cause) {
      setRowError({ id: approving.id, message: (cause as Error).message });
      setApproving(null);
    } finally {
      setBusyId(null);
    }
  };

  const activate = async () => {
    if (!activating) return;
    setBusyId(activating.id);
    try {
      const result = await studioApi.versions.activate(institutionId, activating.id);
      setActivating(null);
      reload();
      onActivated();
      toast.success(
        result.superseded
          ? t('studio.versions.activatedSupersedingToast', {
              number: result.activated.versionNumber,
              previous: result.superseded.versionNumber,
            })
          : t('studio.versions.activatedToast', { number: result.activated.versionNumber }),
      );
    } catch (cause) {
      setRowError({ id: activating.id, message: (cause as Error).message });
      setActivating(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel
      title={t('studio.versions.title')}
      icon="history"
      actions={
        <button
          type="button"
          className={shared.primaryBtnSm}
          onClick={createDraft}
          disabled={creating || Boolean(openDraft)}
          // A disabled button that will not say why is a dead end; the reason rides on the
          // control for pointer and screen reader alike.
          title={openDraft ? t('studio.versions.draftAlreadyOpen', { number: openDraft.versionNumber }) : undefined}
        >
          <Icon name="plus" size={15} />
          {creating ? t('platform.confirm.submitting') : t('studio.versions.newDraft')}
        </button>
      }
    >
      {openDraft && (
        <p className={shared.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('studio.versions.draftAlreadyOpen', { number: openDraft.versionNumber })}
        </p>
      )}
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('studio.versions.emptyTitle')}
        emptyBody={t('studio.versions.emptyBody')}
      >
        {(data) => (
          <ul className={shared.rowList}>
            {data.map((version) => (
              <VersionRow
                key={version.id}
                version={version}
                busy={busyId === version.id}
                error={rowError?.id === version.id ? rowError.message : null}
                onAction={(action) => {
                  setRowError(null);
                  if (action === 'edit') setEditing(version);
                  else if (action === 'preview') setPreviewing(version);
                  else if (action === 'submit') void submit(version);
                  else if (action === 'approve') setApproving(version);
                  else if (action === 'schedule') setScheduling(version);
                  else if (action === 'activate') setActivating(version);
                }}
              />
            ))}
          </ul>
        )}
      </AsyncPanel>

      {editing && (
        <DraftEditorDialog
          institutionId={institutionId}
          version={editing}
          registry={registry}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
            toast.success(t('studio.editor.savedToast', { number: editing.versionNumber }));
          }}
        />
      )}

      {previewing && (
        <PreviewDialog
          institutionId={institutionId}
          version={previewing}
          registry={registry}
          activeResolved={activeResolved}
          onClose={() => setPreviewing(null)}
        />
      )}

      {scheduling && (
        <ScheduleDialog
          institutionId={institutionId}
          version={scheduling}
          onClose={() => setScheduling(null)}
          onScheduled={(scheduled) => {
            setScheduling(null);
            reload();
            toast.success(
              t('studio.versions.scheduledToast', {
                number: scheduled.versionNumber,
                when: scheduled.effectiveFrom ?? '',
              }),
            );
          }}
        />
      )}

      {approving && (
        <ConfirmActionDialog
          open
          title={t('studio.versions.approveTitle', { number: approving.versionNumber })}
          intro={t('studio.versions.approveIntro')}
          tone="neutral"
          impactStatus="ready"
          impact={
            <ul className={shared.impactList}>
              <li>{t('studio.versions.approveImpactNext')}</li>
              <li>{t('studio.versions.approveImpactAuthor')}</li>
            </ul>
          }
          confirmLabel={t('studio.versions.approve')}
          submitting={busyId === approving.id}
          error={rowError?.id === approving.id ? rowError.message : null}
          onCancel={() => setApproving(null)}
          onConfirm={() => void approve()}
        />
      )}

      {activating && (
        <ConfirmActionDialog
          open
          title={t('studio.versions.activateTitle', { number: activating.versionNumber })}
          intro={t('studio.versions.activateIntro')}
          tone="warning"
          impactStatus="ready"
          impact={
            <ul className={shared.impactList}>
              <li>
                {activeVersion
                  ? t('studio.versions.activateImpactSupersede', { number: activeVersion.versionNumber })
                  : t('studio.versions.activateImpactFirst')}
              </li>
              <li>{t('studio.versions.activateImpactImmediate')}</li>
              <li>{t('studio.versions.activateImpactRecords')}</li>
            </ul>
          }
          confirmLabel={t('studio.versions.activate')}
          submitting={busyId === activating.id}
          error={rowError?.id === activating.id ? rowError.message : null}
          onCancel={() => setActivating(null)}
          onConfirm={() => void activate()}
        />
      )}
    </Panel>
  );
}

/** The verbs, offered strictly from the state machine's legal moves. */
function VersionRow({
  version,
  busy,
  error,
  onAction,
}: {
  version: ConfigurationVersion;
  busy: boolean;
  error: string | null;
  onAction: (action: VersionAction) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const actions = VERSION_ACTIONS[version.state] ?? [];

  const actionLabel: Record<VersionAction, string> = {
    edit: t('studio.versions.edit'),
    preview: t('studio.versions.preview'),
    submit: t('studio.versions.submit'),
    approve: t('studio.versions.approve'),
    schedule: t('studio.versions.schedule'),
    activate: t('studio.versions.activate'),
  };

  return (
    <li className={shared.row}>
      <div className={shared.rowMain}>
        <p className={shared.rowTitle}>
          {t('studio.versions.versionNumber', { number: version.versionNumber })}
        </p>
        <p className={shared.rowMetaLine}>
          {t('studio.versions.createdMeta', { when: formatDateTime(version.createdAt, locale) })}
          {version.createdBy ? ` · ${version.createdBy}` : ''}
          {version.approvedAt
            ? ` · ${t('studio.versions.approvedMeta', { when: formatDateTime(version.approvedAt, locale) })}`
            : ''}
          {version.state === 'SCHEDULED' && version.effectiveFrom
            ? ` · ${t('studio.versions.effectiveMeta', { when: formatDateTime(version.effectiveFrom, locale) })}`
            : ''}
          {version.state === 'SUPERSEDED' && version.supersededAt
            ? ` · ${t('studio.versions.supersededMeta', { when: formatDateTime(version.supersededAt, locale) })}`
            : ''}
        </p>
        {version.changeReason && (
          <p className={shared.rowDetail}>{t('studio.versions.reason', { reason: version.changeReason })}</p>
        )}

        {/* The reasons validation refused it, verbatim — the state exists so the editor can
            be told why without re-running anything. */}
        {version.state === 'VALIDATION_FAILED' && version.validationErrors.length > 0 && (
          <ul className={own.validationList} role="alert">
            {version.validationErrors.map((message) => (
              <li key={message} className={shared.rowDetailError}>
                {message}
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p className={shared.rowDetailError} role="alert">
            {error}
          </p>
        )}

        {actions.length > 0 && (
          <div className={shared.transitionRow}>
            {actions.map((action) => (
              <button
                key={action}
                type="button"
                className={action === 'activate' ? shared.primaryBtnSm : shared.secondaryBtn}
                disabled={busy}
                onClick={() => onAction(action)}
                aria-label={`${actionLabel[action]} — ${t('studio.versions.versionNumber', { number: version.versionNumber })}`}
              >
                {busy && action === 'submit' ? t('platform.confirm.submitting') : actionLabel[action]}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className={shared.rowAside}>
        <VersionBadge state={version.state} />
      </div>
    </li>
  );
}

/* ─── Draft editor ─── */

/**
 * Editing a draft's institution overrides: nine Selects, each constrained to the key's
 * closed value set, with "Inherit" as the explicit empty option.
 *
 * "Inherit" MEANS ABSENT. The backend's override map is sparse — a key the institution takes
 * no position on must not appear at all, because an entry would overwrite the country and
 * type layers and the provenance would then blame the institution for a value it never
 * chose. So the empty option removes the entry rather than writing an empty string.
 *
 * Campus overrides and untouched fields (a note, an explicit campusOverridable) are carried
 * through unmodified: this form edits what it shows and refuses to silently drop what it
 * does not.
 */
function DraftEditorDialog({
  institutionId,
  version,
  registry,
  onClose,
  onSaved,
}: {
  institutionId: string;
  version: ConfigurationVersion;
  registry: ConfigurationKeysResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const descriptor of registry.keys) {
      initial[descriptor.key] = version.institutionOverrides[descriptor.key]?.value ?? '';
    }
    return initial;
  });
  const [changeReason, setChangeReason] = useState(version.changeReason ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const overrides: ConfigurationValueMap = {};
      for (const descriptor of registry.keys) {
        const value = values[descriptor.key];
        if (!value) continue; // Inherit: the key is absent, not empty.
        const existing = version.institutionOverrides[descriptor.key];
        overrides[descriptor.key] = { ...existing, value };
      }
      await studioApi.versions.updateDraft(institutionId, version.id, {
        institutionOverrides: overrides,
        changeReason: changeReason.trim() || undefined,
      });
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('studio.editor.title', { number: version.versionNumber })}
      description={t('studio.editor.lead')}
      closeOnBackdropClick={false}
    >
      <form className={shared.dialogForm} onSubmit={save} noValidate>
        {registry.keys.map((descriptor) => (
          <div className={shared.field} key={descriptor.key}>
            <span className={shared.fieldLabel} id={`editor-${descriptor.key}`}>
              {descriptor.label}
            </span>
            <Select
              aria-labelledby={`editor-${descriptor.key}`}
              value={values[descriptor.key] ?? ''}
              onChange={(next) => setValues((current) => ({ ...current, [descriptor.key]: next }))}
              options={[
                { value: '', label: t('studio.editor.inherit'), description: t('studio.editor.inheritHint') },
                ...descriptor.allowedValues.map((allowed) => ({
                  value: allowed,
                  label: humanizeValue(allowed),
                  description: allowed,
                })),
              ]}
            />
            {version.institutionOverrides[descriptor.key]?.note && (
              <span className={shared.fieldHint}>
                {t('studio.editor.existingNote', {
                  note: version.institutionOverrides[descriptor.key]?.note ?? '',
                })}
              </span>
            )}
          </div>
        ))}

        <label className={shared.field}>
          <span className={shared.fieldLabel}>{t('studio.editor.changeReason')}</span>
          <textarea
            className={shared.textarea}
            rows={2}
            value={changeReason}
            onChange={(event) => setChangeReason(event.target.value)}
          />
          <span className={shared.fieldHint}>{t('studio.editor.changeReasonHint')}</span>
        </label>

        {error && (
          <p className={shared.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={submitting}>
            {submitting ? t('platform.confirm.submitting') : t('studio.editor.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Preview with diff ─── */

/**
 * A dry-run resolution of the draft against today's layers, read side by side with what is
 * actually in force. A changed value is shown as before → after and highlighted — the diff
 * IS the review, because "what will this change" is the approver's whole question.
 */
function PreviewDialog({
  institutionId,
  version,
  registry,
  activeResolved,
  onClose,
}: {
  institutionId: string;
  version: ConfigurationVersion;
  registry: ConfigurationKeysResponse;
  activeResolved: ResolvedConfiguration | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const fetcher = useCallback(
    () => studioApi.versions.preview(institutionId, version.id),
    [institutionId, version.id],
  );
  const { state, reload } = useAsyncData<ResolvedConfiguration>(
    `studio-preview:${institutionId}:${version.id}`,
    fetcher,
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t('studio.preview.title', { number: version.versionNumber })}
      description={
        activeResolved
          ? t('studio.preview.leadDiff', { active: activeResolved.versionNumber ?? '' })
          : t('studio.preview.leadNoActive')
      }
    >
      <AsyncPanel state={state} onRetry={reload}>
        {(preview) => (
          <ul className={own.keyList}>
            {registry.keys.map((descriptor) => {
              const after = preview.values[descriptor.key];
              const before = activeResolved?.values[descriptor.key];
              if (!after) {
                return (
                  <li className={own.keyRow} key={descriptor.key}>
                    <div className={own.keyMain}>
                      <p className={own.keyLabel}>{descriptor.label}</p>
                      <p className={shared.rowDetailError}>{t('studio.effective.unresolvedKey')}</p>
                    </div>
                  </li>
                );
              }
              const changed = Boolean(before) && before?.value !== after.value;
              return (
                <PreviewRow
                  key={descriptor.key}
                  descriptor={descriptor}
                  before={before}
                  after={after}
                  changed={changed}
                />
              );
            })}
          </ul>
        )}
      </AsyncPanel>
    </Modal>
  );
}

function PreviewRow({
  descriptor,
  before,
  after,
  changed,
}: {
  descriptor: ConfigurationKeyDescriptor;
  before: ResolvedConfigurationValue | undefined;
  after: ResolvedConfigurationValue;
  changed: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <li className={`${own.keyRow} ${changed ? own.keyRowChanged : ''}`}>
      <div className={own.keyMain}>
        <p className={own.keyLabel}>{descriptor.label}</p>
        <p className={own.diffLine}>
          {changed && before ? (
            <>
              <span className={own.diffBefore} title={before.value}>
                {humanizeValue(before.value)}
              </span>
              <span className={own.diffArrow} aria-hidden="true">
                →
              </span>
              <span className={own.diffAfter} title={after.value}>
                {humanizeValue(after.value)}
              </span>
              <span className={own.srOnly}>
                {t('studio.preview.changedAria', {
                  from: humanizeValue(before.value),
                  to: humanizeValue(after.value),
                })}
              </span>
            </>
          ) : (
            <span title={after.value}>{humanizeValue(after.value)}</span>
          )}
        </p>
        <p className={own.keySource}>
          {t('studio.effective.setBy', { layer: after.source.label })}
          <span className={own.scopeTag}>{t(`studio.scope.${after.source.scope}`)}</span>
        </p>
        <button
          type="button"
          className={own.whyToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          <Icon name="chevronDown" size={14} aria-hidden="true" />
          {expanded ? t('studio.effective.hideWhy') : t('studio.effective.showWhy')}
        </button>
        {expanded && (
          <div className={own.ladderWrap}>
            <ProvenanceLadder resolved={after} />
          </div>
        )}
      </div>
      {changed && (
        <div className={shared.rowAside}>
          <span className={own.changedTag}>{t('studio.preview.changedTag')}</span>
        </div>
      )}
    </li>
  );
}

/* ─── Schedule ─── */

function ScheduleDialog({
  institutionId,
  version,
  onClose,
  onScheduled,
}: {
  institutionId: string;
  version: ConfigurationVersion;
  onClose: () => void;
  onScheduled: (scheduled: ConfigurationVersion) => void;
}) {
  const { t } = useTranslation();
  const [when, setWhen] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The backend refuses a past date; the form refuses it FIRST, because a refusal that
  // arrives after submit is error recovery where prevention was available.
  const future = when.length > 0 && new Date(when).getTime() > Date.now();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!future || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const scheduled = await studioApi.versions.schedule(
        institutionId,
        version.id,
        new Date(when).toISOString(),
      );
      onScheduled(scheduled);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('studio.schedule.title', { number: version.versionNumber })}
      description={t('studio.schedule.lead')}
      closeOnBackdropClick={false}
    >
      <form className={shared.dialogForm} onSubmit={submit} noValidate>
        <label className={shared.field}>
          <span className={shared.fieldLabel}>{t('studio.schedule.whenLabel')}</span>
          <input
            className={shared.input}
            type="datetime-local"
            value={when}
            required
            aria-invalid={when.length > 0 && !future}
            onChange={(event) => setWhen(event.target.value)}
          />
          {when.length > 0 && !future ? (
            <span className={shared.fieldError} role="alert">
              {t('studio.schedule.mustBeFuture')}
            </span>
          ) : (
            <span className={shared.fieldHint}>{t('studio.schedule.whenHint')}</span>
          )}
        </label>

        {error && (
          <p className={shared.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!future || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('studio.versions.schedule')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Shared layers ─── */

/**
 * The platform-owned layers: the floor, country defaults, type and subsystem templates.
 *
 * Deliberately simpler than the version editor — a table of layers and a value form —
 * because these edits carry no lifecycle: the backend applies them to the NEXT resolution
 * and never to an active version's frozen snapshot, and that promise is repeated on screen
 * so an operator is not left waiting for a change that correctly is not appearing.
 */
function LayersAdminPanel() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [scope, setScope] = useState<'' | ConfigurationLayerScope>('');
  const [editing, setEditing] = useState<ConfigurationLayer | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const keysFetcher = useCallback(() => studioApi.keys(), []);
  const { state: keysState } = useAsyncData<ConfigurationKeysResponse>('studio-keys', keysFetcher);

  const fetcher = useCallback(() => studioApi.layers.list(scope || undefined), [scope]);
  const { state, reload } = useAsyncData<ConfigurationLayer[]>(`studio-layers:${scope}`, fetcher);

  const registry = keysState.status === 'ready' ? keysState.data : null;

  return (
    <Panel
      title={t('studio.layers.title')}
      icon="platform"
      actions={
        <button
          type="button"
          className={shared.primaryBtnSm}
          onClick={() => setAddingNew(true)}
          disabled={!registry}
        >
          <Icon name="plus" size={15} />
          {t('studio.layers.add')}
        </button>
      }
    >
      <p className={shared.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('studio.layers.frozenNotice')}
      </p>

      <div className={shared.filterBar}>
        <div className={shared.filterField}>
          <label className={shared.filterLabel} id="studio-layer-scope">
            {t('studio.layers.scopeFilter')}
          </label>
          <Select
            aria-labelledby="studio-layer-scope"
            value={scope}
            onChange={(next) => setScope(next as '' | ConfigurationLayerScope)}
            options={[
              { value: '', label: t('studio.layers.allScopes') },
              ...PLATFORM_OWNED_SCOPES.map((owned) => ({
                value: owned,
                label: t(`studio.scope.${owned}`),
              })),
            ]}
          />
        </div>
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('studio.layers.emptyTitle')}
        emptyBody={t('studio.layers.emptyBody')}
      >
        {(layers) => (
          <ul className={shared.rowList}>
            {layers.map((layer) => (
              <li key={layer.id} className={shared.row}>
                <div className={shared.rowMain}>
                  <p className={shared.rowTitle}>{layer.label}</p>
                  <p className={shared.rowMetaLine}>
                    {t(`studio.scope.${layer.scope}`)}
                    {` · ${layer.scopeKey}`}
                    {` · ${t('studio.layers.keysSet', { total: Object.keys(layer.values).length })}`}
                    {layer.updatedAt ? ` · ${formatDateTime(layer.updatedAt, i18n.language)}` : ''}
                  </p>
                </div>
                <div className={shared.rowAside}>
                  {!layer.enabled && <span className={shared.mutedTag}>{t('studio.layers.disabled')}</span>}
                  <button
                    type="button"
                    className={shared.secondaryBtn}
                    onClick={() => setEditing(layer)}
                    disabled={!registry}
                    aria-label={t('studio.layers.editAria', { label: layer.label })}
                  >
                    {t('studio.layers.edit')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>

      {registry && (editing || addingNew) && (
        <LayerEditorDialog
          layer={editing}
          registry={registry}
          onClose={() => {
            setEditing(null);
            setAddingNew(false);
          }}
          onSaved={(saved) => {
            setEditing(null);
            setAddingNew(false);
            reload();
            toast.success(t('studio.layers.savedToast', { label: saved.label }));
          }}
        />
      )}
    </Panel>
  );
}

/**
 * Edit (or create — PUT is upsert) one shared layer.
 *
 * LAYERS ARE SPARSE and the form preserves that: "Inherit" removes the entry, and a layer
 * stating only the certification pathway keeps stating only that. Writing all nine would
 * make a country default silently overwrite type templates on the eight keys it has no view
 * on — the exact failure the backend's entity comment warns about.
 */
function LayerEditorDialog({
  layer,
  registry,
  onClose,
  onSaved,
}: {
  /** Null when creating a new layer; scope and scopeKey become editable. */
  layer: ConfigurationLayer | null;
  registry: ConfigurationKeysResponse;
  onClose: () => void;
  onSaved: (saved: ConfigurationLayer) => void;
}) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<ConfigurationLayerScope>(layer?.scope ?? 'COUNTRY_DEFAULT');
  const [scopeKey, setScopeKey] = useState(layer?.scopeKey ?? '');
  const [label, setLabel] = useState(layer?.label ?? '');
  const [enabled, setEnabled] = useState(layer?.enabled ?? true);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const descriptor of registry.keys) {
      initial[descriptor.key] = layer?.values[descriptor.key]?.value ?? '';
    }
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The platform floor has exactly one instance and its key is '*' by contract.
  const effectiveScopeKey = scope === 'PLATFORM_DEFAULT' ? '*' : scopeKey.trim();
  const valid = label.trim().length > 0 && effectiveScopeKey.length > 0;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const map: ConfigurationValueMap = {};
      for (const descriptor of registry.keys) {
        const value = values[descriptor.key];
        if (!value) continue;
        const existing = layer?.values[descriptor.key];
        map[descriptor.key] = { ...existing, value };
      }
      const saved = await studioApi.layers.upsert(scope, effectiveScopeKey, {
        label: label.trim(),
        values: map,
        enabled,
      });
      onSaved(saved);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={layer ? t('studio.layers.editTitle', { label: layer.label }) : t('studio.layers.addTitle')}
      description={t('studio.layers.editLead')}
      closeOnBackdropClick={false}
    >
      <form className={shared.dialogForm} onSubmit={save} noValidate>
        {!layer && (
          <>
            <div className={shared.field}>
              <span className={shared.fieldLabel} id="layer-scope-label">
                {t('studio.layers.scopeLabel')}
              </span>
              <Select
                aria-labelledby="layer-scope-label"
                value={scope}
                onChange={(next) => setScope(next as ConfigurationLayerScope)}
                options={PLATFORM_OWNED_SCOPES.map((owned) => ({
                  value: owned,
                  label: t(`studio.scope.${owned}`),
                }))}
              />
            </div>
            <label className={shared.field}>
              <span className={shared.fieldLabel}>{t('studio.layers.scopeKeyLabel')}</span>
              <input
                className={shared.input}
                value={scope === 'PLATFORM_DEFAULT' ? '*' : scopeKey}
                disabled={scope === 'PLATFORM_DEFAULT'}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setScopeKey(event.target.value)}
              />
              <span className={shared.fieldHint}>{t('studio.layers.scopeKeyHint')}</span>
            </label>
          </>
        )}

        <label className={shared.field}>
          <span className={shared.fieldLabel}>{t('studio.layers.labelLabel')}</span>
          <input
            className={shared.input}
            value={label}
            required
            maxLength={200}
            onChange={(event) => setLabel(event.target.value)}
          />
          <span className={shared.fieldHint}>{t('studio.layers.labelHint')}</span>
        </label>

        {registry.keys.map((descriptor) => (
          <div className={shared.field} key={descriptor.key}>
            <span className={shared.fieldLabel} id={`layer-${descriptor.key}`}>
              {descriptor.label}
            </span>
            <Select
              aria-labelledby={`layer-${descriptor.key}`}
              value={values[descriptor.key] ?? ''}
              onChange={(next) => setValues((current) => ({ ...current, [descriptor.key]: next }))}
              options={[
                { value: '', label: t('studio.layers.noOpinion'), description: t('studio.layers.noOpinionHint') },
                ...descriptor.allowedValues.map((allowed) => ({
                  value: allowed,
                  label: humanizeValue(allowed),
                  description: allowed,
                })),
              ]}
            />
          </div>
        ))}

        <Checkbox
          label={t('studio.layers.enabledLabel')}
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        {!enabled && <p className={shared.fieldHint}>{t('studio.layers.disabledHint')}</p>}

        {error && (
          <p className={shared.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('studio.layers.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
